import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Shield, Loader2, Save, Settings2, Layers, CheckCircle, ListChecks, FileText } from 'lucide-react';
import { toast } from 'sonner';

const STAGE_TYPE_LABELS = {
  checkbox: 'Checkbox',
  email: 'Email Action',
  popup: 'Script Popup',
  three_way: 'Yes / No / N/A',
  funded_date: 'Funded Date',
};

export default function FieldTemplatesPage() {
  const { isAdmin, isInternal } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [activeConfig, setActiveConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState('fields'); // 'fields' | 'stages' | 'scripts'
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [editingScript, setEditingScript] = useState({ stageKey: '', text: '' });
  const [disclosureTemplates, setDisclosureTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);

  const fetchConfigs = useCallback(async () => {
    try {
      const res = await api.get('/field-configs');
      setConfigs(res.data);
      if (!selectedIndustry && res.data.length > 0) {
        setSelectedIndustry(res.data[0].industry_category);
        setActiveConfig(structuredClone(res.data[0]));
      }
    } catch (err) {
      toast.error('Failed to load field configs');
    } finally {
      setLoading(false);
    }
  }, [selectedIndustry]);

  useEffect(() => { fetchConfigs(); }, []);

  // Disclosure templates
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await api.get('/disclosure-templates');
      setDisclosureTemplates(res.data || []);
    } catch { /* ignore */ }
    finally { setTemplatesLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'scripts') { fetchTemplates(); }
  }, [activeTab, fetchTemplates]);

  // Load companies once
  useEffect(() => {
    api.get('/companies').then(res => {
      const list = res.data?.companies || res.data || [];
      setCompanies(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  const handleCreateTemplate = () => {
    setEditingTemplate({ name: '', industry_category: '', company_id: '', script_text: '', is_default: false });
    setTemplateModalOpen(true);
  };

  const handleEditTemplate = (tmpl) => {
    setEditingTemplate({ ...tmpl });
    setTemplateModalOpen(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this disclosure script?')) return;
    try {
      await api.delete(`/disclosure-templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    if (!editingTemplate.name?.trim() || !editingTemplate.script_text?.trim()) {
      toast.error('Name and script text are required');
      return;
    }
    setSaving(true);
    try {
      if (editingTemplate.id) {
        await api.put(`/disclosure-templates/${editingTemplate.id}`, editingTemplate);
      } else {
        await api.post('/disclosure-templates', editingTemplate);
      }
      toast.success('Script saved');
      setTemplateModalOpen(false);
      fetchTemplates();
    } catch { toast.error('Failed to save script'); }
    finally { setSaving(false); }
  };

  const handleSelectIndustry = (industry) => {
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setSelectedIndustry(industry);
    const config = configs.find(c => c.industry_category === industry);
    setActiveConfig(config ? structuredClone(config) : null);
    setDirty(false);
  };

  // ---- Field toggles ----
  const handleToggleField = (groupIdx, fieldIdx) => {
    if (!activeConfig) return;
    const updated = structuredClone(activeConfig);
    updated.field_groups[groupIdx].fields[fieldIdx].enabled =
      !updated.field_groups[groupIdx].fields[fieldIdx].enabled;
    setActiveConfig(updated);
    setDirty(true);
  };

  // ---- Stage toggles ----
  const handleToggleStage = (stageIdx) => {
    if (!activeConfig) return;
    const updated = structuredClone(activeConfig);
    updated.deal_stages[stageIdx].enabled = !updated.deal_stages[stageIdx].enabled;
    setActiveConfig(updated);
    setDirty(true);
  };

  // ---- Script editing ----
  const handleOpenScript = (stageKey, currentScript) => {
    setEditingScript({ stageKey, text: currentScript || '' });
    setScriptModalOpen(true);
  };

  const handleSaveScript = () => {
    if (!activeConfig) return;
    const updated = structuredClone(activeConfig);
    const stage = updated.deal_stages.find(s => s.key === editingScript.stageKey);
    if (stage) {
      stage.popup_script = editingScript.text;
    }
    setActiveConfig(updated);
    setDirty(true);
    setScriptModalOpen(false);
    toast.success('Script updated (save changes to persist)');
  };

  // ---- Save ----
  const handleSave = async () => {
    if (!activeConfig) return;
    setSaving(true);
    try {
      const payload = {};
      if (activeTab === 'fields' || dirty) {
        payload.field_groups = activeConfig.field_groups;
      }
      if (activeConfig.deal_stages) {
        payload.deal_stages = activeConfig.deal_stages;
      }
      await api.put(`/field-configs/${activeConfig.industry_category}`, payload);
      toast.success(`${activeConfig.label} config saved`);
      setDirty(false);
      const res = await api.get('/field-configs');
      setConfigs(res.data);
    } catch (err) {
      toast.error('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const enabledFieldCount = activeConfig
    ? activeConfig.field_groups.reduce((sum, g) => sum + g.fields.filter(f => f.enabled).length, 0) : 0;
  const totalFieldCount = activeConfig
    ? activeConfig.field_groups.reduce((sum, g) => sum + g.fields.length, 0) : 0;
  const enabledStageCount = activeConfig?.deal_stages
    ? activeConfig.deal_stages.filter(s => s.enabled).length : 0;
  const totalStageCount = activeConfig?.deal_stages?.length || 0;

  if (!isInternal() || !isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="field-templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary">Field Templates</h1>
          <p className="text-sm text-slate-500 mt-1">
            Control which fields and deal stages appear per industry
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Left: Industry selector */}
          <div className="space-y-3">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                  <Layers className="h-4 w-4 text-slate-500" />
                  Industries
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 pt-0">
                {configs.map((config) => {
                  const fCount = config.field_groups.reduce((s, g) => s + g.fields.filter(f => f.enabled).length, 0);
                  const fTotal = config.field_groups.reduce((s, g) => s + g.fields.length, 0);
                  const sCount = config.deal_stages ? config.deal_stages.filter(s => s.enabled).length : 0;
                  const sTotal = config.deal_stages?.length || 0;
                  const isActive = selectedIndustry === config.industry_category;
                  return (
                    <button
                      key={config.industry_category}
                      onClick={() => handleSelectIndustry(config.industry_category)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                        isActive ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`industry-tab-${config.industry_category}`}
                    >
                      <span className="text-sm font-medium block">{config.label}</span>
                      <div className="flex gap-2 mt-0.5">
                        <span className={`text-[10px] ${isActive ? 'text-slate-400' : 'text-slate-400'}`}>
                          {fCount}/{fTotal} fields
                        </span>
                        <span className={`text-[10px] ${isActive ? 'text-slate-400' : 'text-slate-400'}`}>
                          {sCount}/{sTotal} stages
                        </span>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
            <p className="text-[11px] text-slate-400 px-1">
              Companies with no industry set use the General template.
            </p>
          </div>

          {/* Right: Config panel */}
          {activeConfig ? (
            <div className="space-y-4">
              {/* Tab switcher + Save */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setActiveTab('fields')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                      activeTab === 'fields' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-fields"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Fields
                    <Badge variant="outline" className="text-[10px] ml-0.5">{enabledFieldCount}/{totalFieldCount}</Badge>
                  </button>
                  <button
                    onClick={() => setActiveTab('stages')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                      activeTab === 'stages' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-stages"
                  >
                    <ListChecks className="h-3.5 w-3.5" />
                    Deal Stages
                    <Badge variant="outline" className="text-[10px] ml-0.5">{enabledStageCount}/{totalStageCount}</Badge>
                  </button>
                  <button
                    onClick={() => setActiveTab('scripts')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                      activeTab === 'scripts' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid="tab-scripts"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Disclosure Scripts
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {dirty && (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">Unsaved</Badge>
                  )}
                  <Button onClick={handleSave} disabled={saving || !dirty} className="bg-accent hover:bg-gold-dark" data-testid="save-field-config-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </div>

              {/* Fields Tab */}
              {activeTab === 'fields' && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800">
                      <Settings2 className="h-4 w-4 text-slate-500" />
                      {activeConfig.label} — Client Info Fields
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-0">
                    {activeConfig.field_groups.map((group, gIdx) => (
                      <div key={group.group_name}>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{group.group_name}</p>
                        <div className="space-y-1">
                          {group.fields.map((field, fIdx) => (
                            <div key={field.field_name} className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${field.enabled ? 'bg-slate-50' : 'bg-white'}`}
                              data-testid={`field-toggle-${field.field_name}`}
                            >
                              <div className="flex items-center gap-3">
                                {field.enabled ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                ) : (
                                  <div className="h-4 w-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                                )}
                                <div>
                                  <p className={`text-sm font-medium ${field.enabled ? 'text-slate-800' : 'text-slate-400'}`}>{field.label}</p>
                                  <p className="text-[10px] text-slate-400 font-mono">{field.field_name}</p>
                                </div>
                              </div>
                              <Switch checked={field.enabled} onCheckedChange={() => handleToggleField(gIdx, fIdx)} data-testid={`toggle-${field.field_name}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Stages Tab */}
              {activeTab === 'stages' && activeConfig.deal_stages && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800">
                      <ListChecks className="h-4 w-4 text-slate-500" />
                      {activeConfig.label} — Deal Stages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1 pt-0">
                    {activeConfig.deal_stages.map((stage, sIdx) => (
                      <div key={stage.key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${stage.enabled ? 'bg-slate-50' : 'bg-white'}`}
                        data-testid={`stage-toggle-${stage.key}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {stage.enabled ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          ) : (
                            <div className="h-4 w-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${stage.enabled ? 'text-slate-800' : 'text-slate-400'}`}>{stage.label}</p>
                              <Badge variant="outline" className={`text-[9px] px-1.5 ${
                                stage.type === 'popup' ? 'border-amber-300 text-amber-600' :
                                stage.type === 'email' ? 'border-blue-300 text-blue-600' :
                                stage.type === 'three_way' ? 'border-purple-300 text-purple-600' :
                                stage.type === 'funded_date' ? 'border-emerald-300 text-emerald-600' :
                                'border-slate-200 text-slate-400'
                              }`}>
                                {STAGE_TYPE_LABELS[stage.type] || stage.type}
                              </Badge>
                            </div>
                            <p className="text-[10px] text-slate-400 font-mono">{stage.key}</p>
                          </div>
                          {/* Script edit button for popup stages */}
                          {stage.type === 'popup' && stage.enabled && (
                            <button
                              onClick={() => handleOpenScript(stage.key, stage.popup_script)}
                              className="text-amber-500 hover:text-amber-700 transition-colors flex items-center gap-1 text-[10px] font-medium flex-shrink-0"
                              data-testid={`edit-script-${stage.key}`}
                            >
                              <FileText className="h-3.5 w-3.5" />
                              Edit Script
                            </button>
                          )}
                        </div>
                        <Switch checked={stage.enabled} onCheckedChange={() => handleToggleStage(sIdx)} data-testid={`toggle-stage-${stage.key}`} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {activeTab === 'stages' && !activeConfig.deal_stages && (
                <Card>
                  <CardContent className="text-center py-12">
                    <ListChecks className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No deal stages configured for this industry</p>
                  </CardContent>
                </Card>
              )}

              {/* Disclosure Scripts Tab */}
              {activeTab === 'scripts' && (
                <Card data-testid="disclosure-scripts-panel">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800">
                        <FileText className="h-4 w-4 text-amber-500" />
                        Disclosure Scripts
                      </CardTitle>
                      <Button size="sm" onClick={handleCreateTemplate} data-testid="create-disclosure-btn">
                        + New Script
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Priority: Company override &gt; Industry match &gt; Default. Use {'{{client_name}}'}, {'{{company_name}}'}, {'{{deal_amount}}'}, {'{{funded_amount}}'} as placeholders.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-2 pt-0">
                    {templatesLoading ? (
                      <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                    ) : disclosureTemplates.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 text-sm">No disclosure scripts yet. Create one to get started.</div>
                    ) : (
                      disclosureTemplates.map((tmpl) => (
                        <div key={tmpl.id} className="border border-slate-200 rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`disclosure-tmpl-${tmpl.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-slate-800">{tmpl.name}</span>
                              {tmpl.is_default && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Default</Badge>}
                              {tmpl.industry_category && <Badge variant="outline" className="text-[10px]">{tmpl.industry_category}</Badge>}
                              {tmpl.company_id && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Company Override</Badge>}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 truncate">{tmpl.script_text?.slice(0, 120)}...</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(tmpl)} data-testid={`edit-tmpl-${tmpl.id}`}>
                              Edit
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteTemplate(tmpl.id)} data-testid={`delete-tmpl-${tmpl.id}`}>
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Settings2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select an industry to configure</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Script Editor Modal */}
      <Dialog open={scriptModalOpen} onOpenChange={(v) => !v && setScriptModalOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="script-editor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-amber-600" />
              Edit Disclosure Script
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-xs text-slate-500 mb-2">
              This script will be shown in a popup when the rep clicks the stage checkbox. Use [Client Name] and [Company Name] as placeholders.
            </p>
            <Textarea
              value={editingScript.text}
              onChange={(e) => setEditingScript(prev => ({ ...prev, text: e.target.value }))}
              rows={14}
              className="text-sm leading-relaxed"
              placeholder="Enter the script text..."
              data-testid="script-editor-textarea"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setScriptModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveScript} className="bg-amber-600 hover:bg-amber-700" data-testid="save-script-btn">
              <Save className="h-4 w-4 mr-1" />
              Apply Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disclosure Template Create/Edit Modal */}
      <Dialog open={templateModalOpen} onOpenChange={(v) => !v && setTemplateModalOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4 text-amber-600" />
              {editingTemplate?.id ? 'Edit' : 'Create'} Disclosure Script
            </DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Solar Disclosure"
                  className="text-sm"
                  data-testid="tmpl-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Industry (optional)</Label>
                  <select
                    value={editingTemplate.industry_category || ''}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, industry_category: e.target.value }))}
                    className="w-full h-9 text-sm border border-slate-200 rounded-md px-2"
                    data-testid="tmpl-industry-select"
                  >
                    <option value="">— No industry —</option>
                    {configs.map(c => <option key={c.industry_category} value={c.industry_category}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Company Override (optional)</Label>
                  <select
                    value={editingTemplate.company_id || ''}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, company_id: e.target.value }))}
                    className="w-full h-9 text-sm border border-slate-200 rounded-md px-2"
                    data-testid="tmpl-company-select"
                  >
                    <option value="">— No company —</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingTemplate.is_default}
                  onCheckedChange={(v) => setEditingTemplate(prev => ({ ...prev, is_default: v }))}
                  data-testid="tmpl-default-toggle"
                />
                <Label className="text-xs text-slate-600">Set as default fallback</Label>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Script Text</Label>
                <p className="text-[10px] text-slate-400 mb-1">
                  Placeholders: {'{{client_name}}'}, {'{{company_name}}'}, {'{{deal_amount}}'}, {'{{funded_amount}}'}
                </p>
                <Textarea
                  value={editingTemplate.script_text}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, script_text: e.target.value }))}
                  rows={14}
                  className="text-sm leading-relaxed font-mono"
                  placeholder="Enter the disclosure script..."
                  data-testid="tmpl-script-textarea"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-template-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              {editingTemplate?.id ? 'Update Script' : 'Create Script'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
